import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import {
    Checkbox,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
} from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import { GreyBody, OnwardPagination, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import Snackbar from '@/components/Snackbar';
import DebouncedSearch from '@/components/DebouncedSearch';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { Body1 } from '../blocks';
import { useClientUser } from '@/hooks';
import { DELETE_SERVICE_CATALOG, QUERY_SERVICE_CATALOG } from './graphql';
import ServiceCatalogModal from './ServiceCatalogModal';
import { useCatalogServiceRates, useSaveServiceCatalog } from './hooks';
import {
    dimensionOptions,
    filterServiceCatalog,
    findServiceRate,
    formatDimensionKey,
    formatServiceRate,
} from './utils';
import { formatServiceTime } from '@/components/Account/SKUs/utils';
import { serviceTypeLabel } from '@/constants/serviceTypes';

const ROWS_PER_PAGE = 15;

// The service catalog (public.service_catalog) — shipper-owned; each shipper manages their own.
// `readOnly` is the view of another client's catalog (a carrier looking at a partner shipper): no
// add / edit / delete, and no rates, which are the shipper's own pricing.
const ServiceCatalog = ({ clientRef, readOnly = false }) => {
    const { accountType } = useClientUser();
    const isShipper = accountType === 'shipper';
    const serviceRates = useCatalogServiceRates(readOnly ? null : clientRef);
    const showRate = !readOnly && serviceRates.enabled;
    const { save: saveService, saving: saveLoading } = useSaveServiceCatalog(serviceRates);
    const [notification, setNotification] = useState({});
    const [editModal, setEditModal] = useState(null);
    const [deleteRow, setDeleteRow] = useState(null);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');

    const {
        data,
        loading: queryLoading,
        refetch,
    } = useQuery(QUERY_SERVICE_CATALOG, {
        variables: { where: { client_id: { _eq: clientRef } } },
        skip: !clientRef,
        fetchPolicy: 'cache-and-network',
    });
    const services = useMemo(() => data?.service_catalog || [], [data]);
    const filteredServices = useMemo(() => filterServiceCatalog(services, search), [services, search]);

    const pageCount = Math.max(1, Math.ceil(filteredServices.length / ROWS_PER_PAGE));
    const safePage = Math.min(page, pageCount - 1);
    const pagedServices = filteredServices.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

    // Dimension fields are derived from the client's own rows, so they (and the column) disappear
    // for clients whose catalog is unscoped. All of its rows, not the search matches: a search
    // mustn't hide the column or trim the modal's options.
    const dimensionFields = useMemo(() => dimensionOptions(services), [services]);
    const showDimensionKey = dimensionFields.length > 0;
    const columnCount = 6 + (readOnly ? 0 : 1) + (showDimensionKey ? 1 : 0) + (showRate ? 1 : 0);

    // The edited entry's SERVICE_RATE row. Memoized: a new object would reseed the open form.
    const editServiceRate = useMemo(
        () =>
            editModal?.service_id
                ? findServiceRate({ code: editModal.service_code, dimensions: editModal.dimensions }, serviceRates.rows)
                : null,
        [editModal, serviceRates.rows]
    );

    const onError = (message) => (e) => {
        console.error(e);
        captureException(e);
        setNotification({ severity: 'error', message });
    };

    const [deleteService, { loading: deleteLoading }] = useMutation(DELETE_SERVICE_CATALOG, {
        onCompleted: () => {
            refetch();
            setDeleteRow(null);
        },
        onError: onError('Could not delete the service. Please try again.'),
    });

    const handleSave = async (values, ratePayload) => {
        try {
            await saveService({ service_id: editModal?.service_id, client_id: clientRef, ...values }, ratePayload);
            refetch();
            setEditModal(null);
        } catch (e) {
            onError('Could not save the service — a service with that code may already exist.')(e);
        }
    };

    // Defensive: only the owning shipper can edit/delete, even if this ever mounts elsewhere.
    const canManage = (svc) => !readOnly && isShipper && svc.client?.client_id === clientRef;

    const emptyText =
        services.length > 0
            ? 'No services match your search.'
            : queryLoading
            ? 'Loading services…'
            : readOnly
            ? 'No services yet.'
            : 'No services yet — add one to build your catalog.';

    return (
        <div
            css={css`
                width: 100%;
            `}
        >
            <Snackbar open={!!notification.message} handleClose={() => setNotification({})} {...notification} />

            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                <Grid item>
                    <DebouncedSearch
                        placeholder="Search services..."
                        onChange={(value) => {
                            setSearch(value);
                            setPage(0);
                        }}
                        inputProps={{ 'aria-label': 'Search services' }}
                        css={css`
                            min-width: 280px;
                        `}
                    />
                </Grid>
                {readOnly ? null : (
                    <Grid item>
                        <Tooltip title={isShipper ? '' : 'Services are managed by the shipper'}>
                            <span>
                                <PrimaryButton onClick={() => setEditModal({})} disabled={!isShipper}>
                                    Add Service
                                </PrimaryButton>
                            </span>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        {showDimensionKey ? <TableCell>Dimension Key</TableCell> : null}
                        <TableCell align="right">Service Time</TableCell>
                        <TableCell align="center">Req. Confirmation</TableCell>
                        <TableCell align="center">Billable</TableCell>
                        {showRate ? <TableCell align="right">Rate</TableCell> : null}
                        {readOnly ? null : <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredServices.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columnCount}>
                                <GreyBody>{emptyText}</GreyBody>
                            </TableCell>
                        </TableRow>
                    ) : (
                        pagedServices.map((svc) => (
                            <TableRow key={svc.service_id}>
                                <TableCell>{svc.service_code}</TableCell>
                                <TableCell>{svc.description || '--'}</TableCell>
                                <TableCell>{serviceTypeLabel(svc.service_type) || '--'}</TableCell>
                                {showDimensionKey ? (
                                    <TableCell>{formatDimensionKey(svc.dimension_key) || '--'}</TableCell>
                                ) : null}
                                <TableCell align="right">{formatServiceTime(svc)}</TableCell>
                                {/* Read-only state — edits go through the modal. */}
                                <TableCell align="center" padding="checkbox">
                                    <Checkbox
                                        disableRipple
                                        color="primary"
                                        checked={!!svc.require_confirm}
                                        inputProps={{
                                            readOnly: true,
                                            'aria-label': `${svc.service_code} requires confirmation`,
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center" padding="checkbox">
                                    <Checkbox
                                        disableRipple
                                        color="primary"
                                        checked={svc.billable !== false}
                                        inputProps={{ readOnly: true, 'aria-label': `${svc.service_code} billable` }}
                                    />
                                </TableCell>
                                {/* Blank until the rates are ready, so -- only ever means no rate. */}
                                {showRate ? (
                                    <TableCell align="right">
                                        {serviceRates.ready
                                            ? formatServiceRate(
                                                  findServiceRate(
                                                      { code: svc.service_code, dimensions: svc.dimensions },
                                                      serviceRates.rows
                                                  )?.rate
                                              )
                                            : null}
                                    </TableCell>
                                ) : null}
                                {readOnly ? null : (
                                    <TableCell align="right">
                                        {canManage(svc) ? (
                                            <>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Edit ${svc.service_code}`}
                                                        disabled={deleteLoading}
                                                        onClick={() => setEditModal(svc)}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Delete ${svc.service_code}`}
                                                        onClick={() => setDeleteRow(svc)}
                                                        css={css`
                                                            color: ${colors.reds.primary};
                                                        `}
                                                    >
                                                        <DeleteOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <GreyBody>—</GreyBody>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <Grid
                container
                justifyContent="center"
                css={css`
                    padding: 1rem 0;
                `}
            >
                <OnwardPagination count={pageCount} page={safePage + 1} onChange={(_, p) => setPage(p - 1)} />
            </Grid>

            <ServiceCatalogModal
                open={!!editModal}
                service={editModal?.service_id ? editModal : null}
                onClose={() => setEditModal(null)}
                onSave={handleSave}
                loading={saveLoading}
                dimensionFields={dimensionFields}
                serviceRate={editServiceRate}
                serviceRates={serviceRates}
            />

            <NavResponsiveModal open={!!deleteRow} onClose={() => setDeleteRow(null)}>
                <ModalHeader title="Delete Service?" onClose={() => setDeleteRow(null)} />
                <ModalContent>
                    <Body1>
                        Delete &quot;{deleteRow?.service_code}&quot; from your service catalog? This can&apos;t be
                        undone.
                    </Body1>
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={() => setDeleteRow(null)}>Cancel</SecondaryButton>
                    <PrimaryButton
                        disabled={deleteLoading}
                        onClick={() => deleteService({ variables: { service_id: deleteRow.service_id } })}
                    >
                        Delete
                    </PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>
        </div>
    );
};

export default ServiceCatalog;
